import { components, useApi } from "@appretio/api";
import { useQuery } from "@tanstack/react-query";

type CurrentUserDataAccess = components["schemas"]["CurrentUserDataAccess"];

/**
 * Fetches what data reaches the current user: the roles they hold, split into
 * feature-gating roles and brand tokens, and the chains those tokens actually
 * resolve to.
 *
 * Separate from `useCurrentUserPermissions` on purpose — that one is cheap
 * token arithmetic every page calls to hide controls, while this resolves the
 * tenant's authorization table and is only wanted by the page that shows a
 * user their access. There is no default value: an absent answer is not a
 * user with no access, so consumers render the loading and error states
 * rather than a misleading empty one.
 */
export function useCurrentUserDataAccess() {
  const apiClient = useApi();

  return useQuery<CurrentUserDataAccess>({
    queryKey: ["currentUserDataAccess"],
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to load your access"
    },
    queryFn: async () => {
      const response = await apiClient.GET("/v2/auth/me/data-access", {});
      const result = response.data?.result;
      if (!result) throw new Error("No access data returned");
      return result;
    }
  });
}
